import { PropType, computed, defineComponent, onMounted, ref } from 'vue'

import { MsBookmark, MsBookmarkFilled } from '@sunrise/icons'
import { storeToRefs } from '@sunrise/pinia'
import { sourcePoint } from '@sunrise/shared'
import { MLM, trackLink } from '@sunrise/tracking'
import { Components } from '@sunrise/types'

import { Bookmark } from '../../ts/mapper/bookmarkMapper.interface'
import { DecoratedEstate } from '../../ts/mapper/decoratedItemEstate.interface'
import { vendorName } from '../../ts/sourcepoint/vendor.enum'
import { useBookmarkStore } from '../../ts/store/bookmarkStore/bookmarkStoreModule'
import { BookmarkBuckets } from '../../ts/store/bookmarkStore/bookmarkStoreModule.interface'
import { useToastStore } from '../../ts/store/toastStoreModule/toastStoreModule'

import { TextContent } from '../MsToastManager/MsToastManager.enum'
import { Toast } from '../MsToastManager/MsToastManager.interface'
import { buildMlmTrackingData, buildTealiumTrackingData } from './MsLikeButtonTracking'

const BOOKMARK_LIMIT = 20
export default defineComponent({
    name: 'MsLikeButton',
    components: {
        MsBookmark,
        MsBookmarkFilled,
    },
    props: {
        decoratedEstate: {
            type: Object as PropType<DecoratedEstate>,
            required: true,
        },
        componentId: {
            type: String,
            required: true,
        },
        partOfRecommender: {
            type: Boolean,
            default: false,
        },
        isExposeeOnline: {
            type: Boolean,
            default: true,
        },
        alreadyHydrated: {
            type: Boolean,
            default: false,
        },
        isVerticalDesign: {
            type: Boolean,
        },
    },

    setup(props) {
        const consentGiven = ref(false)
        const bookmarkStore = useBookmarkStore()
        const toastStore = useToastStore()
        const { bookmarks } = storeToRefs(bookmarkStore)
        const isMounted = ref(false)
        const hasBeenClicked = ref(false)

        onMounted(() => {
            sourcePoint.watchVendorConsent(vendorName.ms, (hasConsent) => (consentGiven.value = hasConsent))
            isMounted.value = true
        })

        const liked = computed(() => {
            return isMounted.value && isAlreadyLiked(bookmarks.value, props.decoratedEstate)
        })

        function onClickLikeButton(e: MouseEvent) {
            hasBeenClicked.value = true
            if (!consentGiven.value) {
                sendToast(
                    Components.CsToast.ToastType.ERROR,
                    TextContent.NOT_AVAILABLE,
                    props.decoratedEstate.exposeeId,
                )
                return
            }

            trackTealium(e)
            // we just track when exposes are added to the bookmark list
            if (!liked.value) {
                trackMlm()
            }

            updateBookmarkStore()
        }

        function sendToast(type: Components.CsToast.ToastType, textContent: TextContent, exposeeId: string) {
            const toast: Toast = {
                type,
                textContent,
                exposeeId,
            }
            toastStore.addToast(toast)
        }

        function trackTealium(e: MouseEvent) {
            const target = e.currentTarget as HTMLAnchorElement
            const tealiumData = buildTealiumTrackingData(
                props.decoratedEstate,
                !liked.value,
                props.partOfRecommender,
                props.isExposeeOnline,
            )
            trackLink(e, target, tealiumData)
        }

        function trackMlm() {
            const mlmData = buildMlmTrackingData(props.decoratedEstate, props.componentId)
            MLM.trackClick(mlmData)
        }

        function updateBookmarkStore(): void {
            const bookmarks = bookmarkStore.bookmarks
            const bookmarksAmount = bookmarkStore.getAmount
            let type: Components.CsToast.ToastType
            let textContent: TextContent

            if (isAlreadyLiked(bookmarks, props.decoratedEstate)) {
                type = Components.CsToast.ToastType.POSITIVE
                textContent = TextContent.REMOVED
                bookmarkStore.removeBookmark(props.decoratedEstate)
            } else {
                if (bookmarksAmount >= BOOKMARK_LIMIT) {
                    type = Components.CsToast.ToastType.ERROR
                    textContent = TextContent.Full
                } else {
                    type = Components.CsToast.ToastType.POSITIVE
                    textContent = TextContent.ADDED
                    bookmarkStore.addBookmark(props.decoratedEstate)
                }
            }

            sendToast(type, textContent, props.decoratedEstate.exposeeId)
        }

        return {
            onClickLikeButton,
            liked,
            hasBeenClicked,
        }
    },
})

function isAlreadyLiked(bookmarks: BookmarkBuckets, decoratedEstate: DecoratedEstate) {
    return includes(decoratedEstate, bookmarks.immo) || includes(decoratedEstate, bookmarks.own)
}

function includes(subject: DecoratedEstate, items: Bookmark[]): boolean {
    return !!items.find((item) => item.guid === subject.exposeeId)
}
